import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { obtenerProductoPorId, actualizarProducto, crearProducto } from '../../services/productoService';
import { ROUTES, CATEGORIAS } from '../../constants';
import { toNumber } from '../../utils/formatters';
import './Admin.css';

function AdminEditarProducto() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    modelo: '',
    material: '',
    color: '',
    precio: '',
    preciomenor: '',
    stock: '',
    categoria: '',
    imagenURL: '',
    destacado: false
  });

  const esNuevo = location.pathname.includes('/nuevo') || !id;

  useEffect(() => {
    if (esNuevo) {
      // Si es nuevo producto, no cargar datos
      setLoading(false);
      return;
    }

    const fetchProducto = async () => {
      if (!id) {
        setError('ID de producto no válido');
        setLoading(false);
        return;
      }

      try {
        const producto = await obtenerProductoPorId(id);

        if (!producto) {
          setError('Producto no encontrado');
          setLoading(false);
          return;
        }

        const imagenURL = producto.imagenURL || '';
        setFormData({
          modelo: producto.modelo || '',
          material: producto.material || '',
          color: producto.color || '',
          precio: producto.precio != null ? String(producto.precio) : '',
          preciomenor: producto.preciomenor != null ? String(producto.preciomenor) : '',
          stock: producto.stock != null ? String(producto.stock) : '',
          categoria: producto.categoria || '',
          imagenURL: imagenURL,
          destacado: producto.destacado === true
        });
      } catch (e) {
        console.error('Error al cargar producto:', e);
        setError(e.message || 'Error al cargar el producto');
      } finally {
        setLoading(false);
      }
    };

    fetchProducto();
  }, [id, esNuevo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData = {
        modelo: formData.modelo.trim() || null,
        material: formData.material.trim() || null,
        color: formData.color.trim() || null,
        precio: formData.precio ? toNumber(formData.precio) : null,
        preciomenor: formData.preciomenor ? toNumber(formData.preciomenor) : null,
        stock: formData.stock ? toNumber(formData.stock) : null,
        categoria: formData.categoria.trim() || null,
        imagenURL: formData.imagenURL || null,
        destacado: formData.destacado
      };

      if (esNuevo) {
        // Crear nuevo producto
        const productoId = await crearProducto(updateData);
        setSuccess(true);
        setSaving(false);
        
        // Redirigir después de 1.5 segundos
        setTimeout(() => {
          navigate(ROUTES.ADMIN_PRODUCTOS);
        }, 1500);
      } else {
        // Actualizar producto existente
        await actualizarProducto(id, updateData);
        setSuccess(true);
        setSaving(false);
        
        // Redirigir después de 1.5 segundos
        setTimeout(() => {
          navigate(ROUTES.ADMIN_PRODUCTOS);
        }, 1500);
      }
    } catch (e) {
      console.error('Error al procesar producto:', e);
      setError(e.message || (esNuevo ? 'Error al crear el producto. Por favor, intenta nuevamente.' : 'Error al actualizar el producto. Por favor, intenta nuevamente.'));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="admin-section-title mb-0">{esNuevo ? 'Nuevo Producto' : 'Editar Producto'}</h5>
        <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.ADMIN_PRODUCTOS)}>
          Volver al listado
        </Button>
      </div>

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {success && (
        <Alert variant="success" className="mt-3">
          {esNuevo ? 'Producto creado exitosamente. Redirigiendo...' : 'Producto actualizado exitosamente. Redirigiendo...'}
        </Alert>
      )}

      <div className="admin-form-container">
        <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Categoría</Form.Label>
              <Form.Select
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className="admin-form-select"
              >
                <option value="">Seleccionar categoría</option>
                <option value={CATEGORIAS.FUNDAS}>Fundas</option>
                <option value={CATEGORIAS.ACCESORIOS}>Accesorios</option>
                <option value={CATEGORIAS.VIDRIOS}>Vidrios</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Modelo</Form.Label>
              <Form.Control
                type="text"
                name="modelo"
                value={formData.modelo}
                onChange={handleChange}
                placeholder="Modelo"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>

          {(formData.categoria === CATEGORIAS.FUNDAS || formData.categoria === CATEGORIAS.VIDRIOS) && (
            <>
              <Col md={4}>
                <Form.Group className="admin-form-group">
                  <Form.Label className="admin-form-label">Material</Form.Label>
                  <Form.Control
                    type="text"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    placeholder="Material"
                    className="admin-form-control"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="admin-form-group">
                  <Form.Label className="admin-form-label">Color</Form.Label>
                  <Form.Control
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="Color"
                    className="admin-form-control"
                  />
                </Form.Group>
              </Col>
            </>
          )}

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Precio (Mayorista)</Form.Label>
              <Form.Control
                type="number"
                name="precio"
                value={formData.precio}
                onChange={handleChange}
                placeholder="0"
                step="0.01"
                min="0"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Precio (Minorista)</Form.Label>
              <Form.Control
                type="number"
                name="preciomenor"
                value={formData.preciomenor}
                onChange={handleChange}
                placeholder="0"
                step="0.01"
                min="0"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Stock</Form.Label>
              <Form.Control
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                step="1"
                min="0"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">URL de Imagen</Form.Label>
              <Form.Control
                type="text"
                name="imagenURL"
                value={formData.imagenURL}
                onChange={handleChange}
                placeholder="/images/nombre-imagen.png o https://..."
                className="admin-form-control"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <div className="border rounded p-3 admin-checkbox-container" style={{ backgroundColor: '#f8f9fa' }}>
              <Form.Group className="mb-0 d-flex align-items-center justify-content-between w-100">
                <Form.Check
                  type="checkbox"
                  name="destacado"
                  label={<strong style={{ fontSize: '1.1rem' }}>Producto destacado</strong>}
                  checked={formData.destacado}
                  onChange={handleChange}
                  style={{ fontSize: '1.2rem' }}
                  className="admin-checkbox-destacado mb-0"
                />
                <Form.Text className="text-muted mb-0 ms-3">
                  Los productos destacados aparecen en la sección principal de la página de inicio
                </Form.Text>
              </Form.Group>
            </div>
          </Col>
        </Row>

        <div className="admin-form-actions d-flex gap-2">
          <Button
            variant="primary"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {esNuevo ? 'Creando producto...' : 'Guardando...'}
              </>
            ) : (
              esNuevo ? 'Crear Producto' : 'Guardar Cambios'
            )}
          </Button>
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_PRODUCTOS)}
            disabled={saving}
          >
            Cancelar
          </Button>
        </div>
      </Form>
      </div>
    </Container>
  );
}

export default AdminEditarProducto;
